import { useStyletron } from "baseui";
import { Block } from "baseui/block";
import { FlexGrid, FlexGridItem } from 'baseui/flex-grid';
import { FormControl } from 'baseui/form-control';
import { ChevronLeft, ChevronRight, Delete } from "baseui/icon";
import { DURATION, useSnackbar } from "baseui/snackbar";
import { HeadingXLarge, ParagraphMedium } from 'baseui/typography';
import { forwardRef, useEffect, useRef, useState } from "react";
import { useDispatch } from 'react-redux';
import { submitSolution, uploadSolutionPhotos } from "../application/actions/solutions";
import { routeReplace } from "../application/actions/ui";
import { ReactComponent as Diagram } from '../assets/images/diagram.svg';
import useBreakpoint from "../hooks/useBreakpoint";
import useLocale from "../hooks/useLocale";
import useFormBuffer from "../hooks/useFormBuffer";
import ReDoButton from "./ReDoButton";
import ReDoFileUpload from "./ReDoFileUpload";
import ReDoInterests from "./ReDoInterests";
import ReDoLogo from "./ReDoLogo";
import ReDoProgressBar from "./ReDoProgressBar";
import ReDoSpinner from './ReDoSpinner';
import ReDoTags from './ReDoTags';
import ReDoTextarea from "./RedoTextarea";


const SOLVE_BG = '#CABEFF';

const areas = [
  'Energy',
  'Water',
  'Waste',
  'Procurement',
  'Materials',
  'Communication',
  'Supply Chains',
  'Packaging',
  'End of Life',
  'Infrastructure',
  'Climate Adaption',
  'Animals',
]

const solutionStage = [
  { group: '', list: ['EARLY \n(Idea or concept stage)', 'MID \n(First proof of concept exists, first user testing done)', 'FINAL \n(Prototype exists, research and testing results exist)'], color: '#fff' },
]

const ReDoSolveChallenge = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const [css, theme] = useStyletron();
  const breakpoint = useBreakpoint()
  const challenge = props.challenge || {};

  const { enqueue } = useSnackbar();
  const { t } = useLocale();
  const { readBuffer, writeBuffer, clearBuffer } = useFormBuffer('solveChallenge');

  const stages = [
    { label: t('solveChallenge.stage_title'),      curr: 1, max: 6 },
    { label: t('solveChallenge.stage_solution'),   curr: 2, max: 6 },
    { label: t('solveChallenge.stage_life_cycle'), curr: 3, max: 6 },
    { label: t('solveChallenge.stage_areas'),      curr: 4, max: 6 },
    { label: t('solveChallenge.stage_visuals'),    curr: 5, max: 6 },
    { label: t('solveChallenge.stage_stage'),      curr: 6, max: 6 },
  ]

  const saved = readBuffer();
  const [stageCurr, setStageCurr] = useState(saved ? saved.stage : 1)
  const stage = stages.find(s => s.curr === stageCurr) || stages[0]
  const [payload, setPayload] = useState(saved?.payload || props.solution || {})

  const [photos, setPhotos] = useState([])

  const [loading, setLoading] = useState(false)

  const scrollable = useRef()

  useEffect(() => {
    writeBuffer(stageCurr, payload);
  }, [stageCurr, payload, writeBuffer])

  const column = css({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    justifyContent: 'flex-start',
    marginTop: breakpoint === 'large' ? '7ch' : '2ch',
    gap: breakpoint === 'large' ? '3ch' : '1.5ch'
  });

  const onNext = (e) => {
    e.preventDefault();
    scrollable.current && scrollable.current.scrollTo(0, 0)

    const data = new FormData(e.target);
    const stepPayload = {}

    for (const pair of data.entries()) {
      if (stageCurr === 6 && pair[0] === 'stage') {
        stepPayload[pair[0]] = pair[1]?.toLowerCase()?.split(' ')?.[0]
      } else {
        stepPayload[pair[0]] = pair[1]
      }
    }

    const finalPayload = { ...payload, ...stepPayload }

    setPayload(finalPayload)

    const currStage = stages.findIndex(s => s.curr === stageCurr)
    if (currStage < stages.length - 1) {
      setStageCurr(stages[currStage + 1].curr)
    } else {
      setLoading(true)
      const challengeSolutionPayload = {
        target: challenge.target || 'the challenge creator',
        problem: challenge.problem || challenge.title,
        what: challenge.title,
        createdValue: challenge.goal || finalPayload.summary || finalPayload.description,
        communityReason: finalPayload.summary || finalPayload.description,
        isChallengeSolution: true,
        visibility: 'company',
        projectType: 'challengeSolution',
        ...finalPayload,
      }

      if (challenge.id) {
        challengeSolutionPayload.challengeId = challenge.id
        challengeSolutionPayload.challengeID = challenge.id
      }

      const withChallengeLink = (solution) => challenge.id ? { ...solution, challengeId: challenge.id, challengeID: challenge.id } : solution

      dispatch(submitSolution(challengeSolutionPayload)).then(async res => {
        let solution = withChallengeLink(res)
        if (photos.length) {
          for (const photo of photos) {
            try {
              const photoRes = await dispatch(uploadSolutionPhotos({ solution: solution.id, title: photo.file?.file?.name ?? photo.file?.name ?? photo.title, photo: photo.photo }))
              solution = withChallengeLink(photoRes)
            } catch (e) {
              setLoading(false)
              enqueue({ message: e.message + ' ' + e.response?.data?.error, dismissAfter: DURATION.shortest, startEnhancer: () => <Delete size={theme.sizing.scale800} /> });
            }
          }
        }
        setLoading(false)
        clearBuffer()
        // replace (not push) so back from the submitted solution skips the solve form and returns to the challenge/main
        dispatch(routeReplace({ view: 'solution', payload: { solution } }))
      }).catch(e => {
        setLoading(false)
        enqueue({ message: e.response?.data?.error || e.message, dismissAfter: DURATION.shortest, startEnhancer: () => <Delete size={theme.sizing.scale800} /> })
      })

    }
  }

  const onPrev = (e) => {
    e.preventDefault();
    scrollable.current && scrollable.current.scrollTo(0, 0)

    const data = new FormData(scrollable.current);
    const stepPayload = {}

    for (const pair of data.entries()) {
      if (stageCurr === 6 && pair[0] === 'stage') {
        stepPayload[pair[0]] = pair[1]?.toLowerCase()?.split(' ')?.[0]
      } else {
        stepPayload[pair[0]] = pair[1]
      }
    }

    const updated = { ...payload, ...stepPayload }
    setPayload(updated)

    const currStage = stages.findIndex(s => s.curr === stageCurr)
    if (currStage > 0) {
      setStageCurr(stages[currStage - 1].curr)
    }
  }

  const onUpload = (e) => {
    setPhotos(e.filter(f => f.file));
  };

  const renderStageFields = (shadow) => (
    <>
      {stage.curr === 1 &&
        <>
          <Block width='100%' marginTop='5%'>
            <FormControl>
              <ReDoTextarea
                label={t('solveChallenge.title_label')}
                placeholder={t('solveChallenge.title_placeholder')}
                name='title'
                required
                value={payload.title}
                maxLength={20}
                shadow={shadow}
              />
            </FormControl>
          </Block>
          <Block width='100%' marginTop='5%'>
            <FormControl>
              <ReDoTextarea
                label={t('solveChallenge.summary_label')}
                placeholder={t('solveChallenge.summary_placeholder')}
                maxLength={65}
                name='summary'
                required
                value={payload.summary}
                shadow={shadow}
              />
            </FormControl>
          </Block>
        </>
      }

      {stage.curr === 2 &&
        <>
          <Block width='100%' marginTop='5%'>
            <FormControl>
              <ReDoTextarea
                label={t('solveChallenge.description_label')}
                placeholder={t('solveChallenge.type_here')}
                maxLength={500}
                name='description'
                required
                value={payload.description}
                shadow={shadow}
              />
            </FormControl>
          </Block>
          <Block width='100%' marginTop='5%'>
            <FormControl>
              <ReDoTextarea
                label={t('solveChallenge.note_label')}
                placeholder={t('solveChallenge.type_here')}
                maxLength={500}
                name='note'
                value={payload.note}
                shadow={shadow}
              />
            </FormControl>
          </Block>
        </>
      }

      {stage.curr === 3 &&
        <>
          <Block width='100%' marginTop='5%'>
            <ParagraphMedium marginTop='0' paddingLeft='5px'>
              {t('solveChallenge.lifecycle_text')}
            </ParagraphMedium>
            <Diagram aria-hidden="true" style={{ display: 'block', width: '100%', maxWidth: breakpoint === 'large' ? '100%' : '220px', height: 'auto', margin: breakpoint === 'large' ? '0' : '0 auto 8px' }} />
            <a className={css({ textUnderlineOffset: '.5ch', fontWeight: 'normal', fontSize: '10px', color: theme.colors.secondary200 })} href="https://blog.redo-please.com/lifecycle-thinking/" target="_blank" rel="noopener noreferrer">{t('solveChallenge.lifecycle_link')}</a>
          </Block>
          <Block width='100%' marginTop='5%'>
            <FormControl>
              <ReDoTextarea
                id="lifeCycle"
                placeholder={t('solveChallenge.type_here')}
                name='lifeCycle'
                value={payload.lifeCycle}
                maxLength={500}
                shadow={shadow}
              />
            </FormControl>
          </Block>
        </>
      }

      {stage.curr === 4 &&
        <Block width='100%' marginTop='5%'>
          <FormControl>
            <ReDoTags
              onChange={(e) => setPayload({ ...payload, areas: e.map(v => v.toLowerCase()) })}
              label={t('solveChallenge.areas_label')}
              options={areas}
              limit={1}
              additionLabel={t('solveChallenge.areas_add')}
              values={payload.areas}
              submitUpTo={4}
            />
          </FormControl>
        </Block>
      }

      {stage.curr === 5 &&
        <Block width='100%' marginTop='5%'>
          <FormControl>
            <ReDoFileUpload
              multiple
              maxCount={3}
              onUpload={onUpload}
              onRemove={onUpload}
              label={t('solveChallenge.visuals_label')}
              values={payload?.photos?.map(f => ({ ...f, preview: f.photo }))}
            />
          </FormControl>
        </Block>
      }

      {stage.curr === 6 &&
        <Block width='100%' marginTop='5%'>
          <ReDoInterests
            label={t('solveChallenge.select_stage_label')}
            options={solutionStage}
            values={payload?.stage}
            name='stage'
            radio
          />
        </Block>
      }
    </>
  )

  const navButtons = (
    <Block paddingBottom={breakpoint === 'large' ? '50px' : '0'} justifyContent='space-between' display='flex' width='100%' marginTop='5%'>
      <ReDoButton
        type='button'
        onClick={stage.curr === 1 ? () => window.history.back() : onPrev}
        kind='inverse'
        overrides={{
          BaseButton: {
            style: {
              marginTop: '0',
              marginBottom: '0',
              paddingLeft: '0 !important',
              paddingTop: '0 !important',
              paddingBottom: '0 !important',
              backgroundColor: 'transparent',
              color: theme.colors.black,
              width: 'auto',
              fontWeight: 'bold'
            }
          },
          StartEnhancer: { style: { marginRight: '0' } }
        }}
        startEnhancer={() => <ChevronLeft size={theme.sizing.scale1000} />}
      >
        {t('common.back')}
      </ReDoButton>
      <ReDoButton
        type='submit'
        kind='brand'
        overrides={{
          BaseButton: {
            style: {
              marginBottom: '0',
              paddingTop: '5px',
              paddingBottom: '5px',
              paddingRight: '0',
              width: 'auto',
              marginLeft: 'auto'
            }
          },
          EndEnhancer: { style: { marginLeft: '0' } }
        }}
        endEnhancer={() => <ChevronRight size={theme.sizing.scale1000} />}
      >
        {stageCurr !== stages.length ? t('common.next') : t('common.submit')}
      </ReDoButton>
    </Block>
  )

  if (breakpoint === 'large') {
    return (
      <Block
        ref={ref}
        position='relative'
        height='100vh'
        overflow='hidden'
        backgroundImage={`linear-gradient(90deg, white 0%, white 50%, ${SOLVE_BG} 50%, ${SOLVE_BG} 100%)`}
      >
        {loading && <ReDoSpinner />}
        <Block position='absolute' top='28px' right='56px' overrides={{ Block: { style: { zIndex: 3 } } }}>
          <ReDoLogo size={theme.sizing.scale2400} />
        </Block>
        <FlexGrid
          flexGridColumnCount={[1, 1, 1, 2]}
          flexGridRowGap="scale800"
          height='100%'
          alignItems='center'
          overflow='hidden'
          margin='0'
        >
          <FlexGridItem position='relative' height='100%' display='flex'>
            <Block width='100%' height='100%' backgroundColor='white'>
              <div className={css({ height: '100%', justifyContent: 'space-between', display: 'flex', flexDirection: 'column', padding: `${theme.sizing.scale1600} ${theme.sizing.scale3200}` })}>
                <div className={css({ paddingTop: theme.sizing.scale1600, maxWidth: '500px' })}>
                  <HeadingXLarge overrides={{ Block: { style: { lineHeight: '48px', fontWeight: '300' } } }}>{t('solveChallenge.heading')}</HeadingXLarge>
                  {stage.curr === 1 &&
                    <Block paddingTop='2ch'>
                      <ParagraphMedium color={theme.colors.primary300}>
                        {t('solveChallenge.intro_text')}
                      </ParagraphMedium>
                    </Block>
                  }
                </div>
                <Block display='flex' flexDirection='column' marginTop='auto'>
                  <ReDoProgressBar label={stage.label} value={stage.curr} maxValue={stage.max} />
                </Block>
              </div>
            </Block>
          </FlexGridItem>

          <FlexGridItem height='100%'>
            <Block height='100%' backgroundColor={SOLVE_BG}>
              <div className={column}>
                <Block position='relative' marginTop={['20px', '20px', '20px', '70px']} height={['auto', 'auto', 'auto', 'calc(100% - 215px)']} minHeight={['calc(100% - 215px)', 'calc(100% - 215px)', 'calc(100% - 215px)', 'auto']}>
                  <Block overrides={{ Block: { style: { zIndex: '1' } } }} display={['none', 'none', 'none', 'block']} position='absolute' top='0px' left='0' width='100%' height='50px' backgroundImage={`linear-gradient(${SOLVE_BG} 0%, rgba(202,190,255,0) 100%)`} />

                  <form ref={scrollable} className={css({ height: '100%', overflow: 'auto' })} onSubmit={onNext}>
                    <div className={css({ paddingTop: '20px', maxWidth: '375px', margin: 'auto', paddingBottom: '28px', alignItems: 'center', display: 'flex', flexDirection: 'column', minHeight: '100%', paddingRight: '1ch' })}>
                      {renderStageFields(false)}
                      {navButtons}
                    </div>
                  </form>

                  <Block display={['none', 'none', 'none', 'block']} position='absolute' bottom='0' left='0' width='100%' height='50px' backgroundImage={`linear-gradient(rgba(202,190,255,0) 0%, ${SOLVE_BG} 100%)`} />
                </Block>
              </div>
            </Block>
          </FlexGridItem>
        </FlexGrid>
      </Block>
    );
  }

  return (
    <>
      {loading && <ReDoSpinner />}
      <Block height='100%' backgroundColor={SOLVE_BG} margin='0 -28px' padding='0 28px'>
        <div ref={scrollable} className={`${column} ${css({ padding: '3ch 0', overflow: 'auto', height: '100%' })}`}>
          <Block alignSelf='flex-end' justifyContent='space-between' display='flex'>
            <ReDoProgressBar kind='text' label={stage.label} value={stage.curr} maxValue={stage.max} />
          </Block>

          <HeadingXLarge paddingTop='1ch' margin='0'>{stage.curr === 1 ? t('solveChallenge.heading') : stage.label}</HeadingXLarge>

          {stage.curr === 1 &&
            <ParagraphMedium marginTop='0' color={theme.colors.primary300}>
              {t('solveChallenge.intro_text')}
            </ParagraphMedium>
          }

          <Block height='100%'>
            <form className={css({ alignItems: 'center', display: 'flex', flexDirection: 'column', height: '100%' })} onSubmit={onNext}>
              <div className={css({ width: '100%', maxWidth: '375px', paddingBottom: '10ch' })}>
                {renderStageFields(true)}
                {navButtons}
              </div>
            </form>
          </Block>
        </div>
      </Block>
    </>
  )
});

export default ReDoSolveChallenge;
